/**
 * API Service for Backend Integration
 * Handles all API calls to the HealthcarePortal.API backend
 * 
 * Note: Auth-related endpoints (session, user profile) use VITE_AUTH_SERVICE_URL
 * Credentialing endpoints (documents, health) use VITE_CREDENTIALING_API_BASE_URL
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { msalReady } from './msalBootstrap.service';
import { AUTH_API_CONFIG, AUTH_API_ENDPOINTS, CREDENTIALING_API_CONFIG, DATA_ACCESS_API_CONFIG, USER_API_CONFIG } from '../config/api.config';
import { getValidAccessToken, clearTokenCache } from '../utils/tokenCache.utils';
import { logApiCall, logApiError } from '../utils/authTelemetry.utils';
import { getAuthScopes } from '../config/msal.config';
import { tokenRetryGuard } from '../utils/tokenRetryGuard.utils';
import { acquireTokenSilentDeduplicated } from '../utils/tokenAcquisition.utils';
import { notifySessionApiActivityForRequest } from './apiClient.service';

/**
 * Shared token provider reference (prevents interceptor stacking)
 * When setupApiInterceptor is called with a token provider, this reference is updated
 * The interceptor uses this reference instead of creating new interceptors
 */
let getTokenRef: (() => Promise<string | null>) | undefined;

/**
 * Module-level getter for isLoggingIn state
 * This is set by AuthContext and checked by the interceptor
 * CRITICAL: This must be set from AuthContext to keep it in sync
 */
let getIsLoggingInRef: (() => boolean) | undefined;

/**
 * Set the isLoggingIn getter function
 * Called from AuthContext to keep state in sync
 */
export const setIsLoggingInGetter = (getter: () => boolean) => {
  getIsLoggingInRef = getter;
};

/**
 * Check if a URL is allowed during redirect processing (isLoggingIn === true).
 * UserService validation and session API calls are allowed; all others fail-fast.
 */
function isAllowedDuringRedirect(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/api/v1/users') ||
    url.includes('/api/v1/clinic-staff-profiles') ||
    url.includes('/api/user') ||
    url.includes('/userProfile') ||
    url.includes('/session')
  );
}

/**
 * Registration and other endpoints that are allowed without an access token.
 * Backend marks these with [AllowAnonymous] or equivalent.
 */
function isRegistrationOrPublicEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  const u = url.toLowerCase();
  if (u.includes('/api/v1/users/check-npi') || u.includes('/api/v1/providers/validate')) return true;
  return false;
}

/**
 * Extract base URL from environment variable, removing any API path
 * Handles URLs with or without version paths (e.g., https://localhost:7043/api/v1)
 * Returns just the origin (e.g., https://localhost:7043)
 */
const extractBaseUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.origin; // Returns protocol + host + port, no path
  } catch {
    return url;
  }
};

/**
 * Get base URL for API client
 * In development, use relative URLs to leverage Vite proxy (bypasses CORS)
 * In production, use absolute URLs from environment variables (without API path)
 * If envUrl is provided and valid, use it directly even in development
 */
const getBaseUrl = (envUrl: string, fallback: string): string => {
  // If a valid URL is provided, use it (works in both dev and prod)
  if (envUrl && envUrl.trim()) {
    return extractBaseUrl(envUrl);
  }
  // In development, use relative URLs so requests go through Vite proxy
  if (import.meta.env.DEV) {
    return ''; // Empty string = relative URL, will use current origin
  }
  // In production, extract base URL (remove any /api/v* path if present)
  // Endpoints already include the version path, so base URL should be just the origin
  return extractBaseUrl(fallback);
};

/**
 * Create axios instance for CredentialingAPI (NPI validation, health, etc.)
 * Uses VITE_CREDENTIALING_API_BASE_URL
 * In development: uses relative URLs (goes through Vite proxy)
 * In production: uses absolute URLs from environment
 */
const credentialingClient: AxiosInstance = axios.create({
  baseURL: getBaseUrl(CREDENTIALING_API_CONFIG.baseUrl, 'https://credentialing-api.azure-api.net'),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: CREDENTIALING_API_CONFIG.timeout,
});

/**
 * Create axios instance for Auth API (session, user profile, etc.)
 * Uses VITE_AUTH_SERVICE_URL
 * In development: uses relative URLs (goes through Vite proxy)
 * In production: uses absolute URLs from environment
 */
const authClient: AxiosInstance = axios.create({
  baseURL: getBaseUrl(AUTH_API_CONFIG.baseUrl, 'https://auth-api.azure-api.net'),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: AUTH_API_CONFIG.timeout,
});

/**
 * Create axios instance for Data Access API (documents, forms, etc.)
 * Uses VITE_DATA_ACCESS_API_BASE_URL
 * In development: uses relative URLs (goes through Vite proxy)
 * In production: uses absolute URLs from environment
 */
const dataAccessClient: AxiosInstance = axios.create({
  baseURL: getBaseUrl(DATA_ACCESS_API_CONFIG.baseUrl, 'https://data-access-api.azure-api.net'),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: DATA_ACCESS_API_CONFIG.timeout,
});

/**
 * Create axios instance for User API (user registration, profile management, etc.)
 * Uses VITE_API_USER_BASE_URL
 * In development: uses relative URLs (goes through Vite proxy)
 * In production: uses absolute URLs from environment
 */
const userClient: AxiosInstance = axios.create({
  baseURL: getBaseUrl(USER_API_CONFIG.baseUrl, 'https://localhost:7006'),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: USER_API_CONFIG.timeout,
});

/**
 * Setup authentication interceptors for both clients
 * Uses MSAL token cache for efficient token management
 * Automatically handles token refresh via MSAL's acquireTokenSilent
 * 
 * CRITICAL (MUST FIX #1): When isLoggingIn === true, fail-fast all background API calls
 * except UserService validation calls. This makes race conditions visible and prevents
 * noisy 401 storms.
 */
const setupAuthInterceptor = (client: AxiosInstance) => {
  // Request interceptor - add auth token from MSAL
  client.interceptors.request.use(
    async (config) => {
      try {
        // CRITICAL (MUST FIX #1): Fail-fast during redirect processing
        // Check isLoggingIn state if getter is available
        if (getIsLoggingInRef) {
          const isLoggingIn = getIsLoggingInRef();
          
          // Allow only UserService and session API calls during redirect processing
          const isAllowed = isAllowedDuringRedirect(config.url);

          if (isLoggingIn && !isAllowed) {
            // Fail-fast: Treat background requests during redirect as bugs, not recoverable
            // This makes race conditions visible and prevents noisy 401 storms
            throw new Error('Authentication in progress. Please wait.');
          }
        }

        // Only add token for MSAL authentication
        if (AUTH_API_CONFIG.providerType === 'msal') {
          const isPublic = isRegistrationOrPublicEndpoint(config.url);
          if (isPublic) {
            // Registration/public endpoints (e.g. check-npi, validate provider) are allowed without token.
            if (import.meta.env.DEV && config.url) {
              console.log('[API Service] Allowing request without token (registration/public):', config.method?.toUpperCase(), config.url);
            }
            return config;
          }

          // CRITICAL (Item 4 Fix): Use shared token provider reference if available
          // This prevents interceptor stacking and allows redirect handler to provide
          // token provider function that doesn't depend on activeAccount
          if (getTokenRef) {
            // Use provided token provider (from redirect handler)
            const token = await getTokenRef();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
              if (config.url) {
                logApiCall(config.url, config.method?.toUpperCase() || 'GET');
                if (import.meta.env.DEV) {
                  console.log(`[API Service] Adding Authorization header to ${config.method?.toUpperCase()} ${config.url} (via token provider)`);
                }
              }
            } else {
              console.warn('[API Service] Token provider returned no token — rejecting request to avoid 401.');
              window.dispatchEvent(new CustomEvent('auth:logout'));
              return Promise.reject(new Error('No access token available. Please sign in again.'));
            }
          } else {
            // Fallback to default token acquisition (for normal app operation)
            // CRITICAL: Await MSAL bootstrap so active account is set before token acquisition.
            const msalInstance = await msalReady;

            if (!msalInstance) {
              console.warn('[API Service] MSAL instance not available — rejecting request.');
              window.dispatchEvent(new CustomEvent('auth:logout'));
              return Promise.reject(new Error('Authentication not ready. Please sign in again.'));
            }

            const accounts = msalInstance.getAllAccounts();
            if (accounts.length === 0) {
              console.warn('[API Service] No authenticated accounts — rejecting request.');
              window.dispatchEvent(new CustomEvent('auth:logout'));
              return Promise.reject(new Error('No authenticated account. Please sign in again.'));
            }

            const token = await getValidAccessToken(msalInstance);
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
              if (config.url) {
                logApiCall(config.url, config.method?.toUpperCase() || 'GET');
                if (import.meta.env.DEV) {
                  console.log(`[API Service] Adding Authorization header to ${config.method?.toUpperCase()} ${config.url}`);
                  console.log(`[API Service] Token length: ${token.length} characters`);
                  try {
                    const { logTokenDetails } = await import('../utils/tokenValidation.utils');
                    logTokenDetails(token);
                  } catch {
                    // Silently fail if token utils not available
                  }
                }
              }
            } else {
              console.warn('[API Service] No access token available (silent acquisition failed) — rejecting request.');
              window.dispatchEvent(new CustomEvent('auth:logout'));
              return Promise.reject(new Error('No access token available. Please sign in again.'));
            }
          }
        } else {
          if (import.meta.env.DEV) {
            console.log(`[API Service] Auth provider type is '${AUTH_API_CONFIG.providerType}', skipping MSAL token`);
          }
        }
      } catch (error) {
        console.error('[API Service] Failed to get auth token:', error);
        if (error instanceof Error && error.message === 'Authentication in progress. Please wait.') {
          throw error;
        }
        // When using MSAL, do not send the request without a token — reject so backend does not get 401 from missing Bearer
        if (AUTH_API_CONFIG.providerType === 'msal') {
          window.dispatchEvent(new CustomEvent('auth:logout'));
          return Promise.reject(error instanceof Error ? error : new Error('Failed to get auth token. Please sign in again.'));
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors and token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any;
      
      if (error.response) {
        // Log API error
        if (error.config?.url) {
          logApiError(
            error.config.url,
            error.message,
            error.response.status
          );
        }
        
        // Server responded with error status
        console.error('[API Service] Server error:', {
          status: error.response.status,
          data: error.response.data,
          url: error.config?.url,
        });

        // Handle 401 - Token expired, try to refresh with retry guard
        if (error.response.status === 401 && !originalRequest._retry && AUTH_API_CONFIG.providerType === 'msal') {
          // Check retry guard before attempting refresh
          if (!tokenRetryGuard.shouldRetry()) {
            console.error('[API Service] Token refresh blocked by retry guard - circuit open');
            window.dispatchEvent(new CustomEvent('auth:logout'));
            return Promise.reject(new Error('Token refresh circuit breaker is open'));
          }

          originalRequest._retry = true;
          
          console.warn('[API Service] Received 401 Unauthorized, attempting token refresh...');
          
          const msalInstance = await msalReady;
          if (msalInstance) {
            try {
              // Force token refresh (MSAL will handle interaction if needed)
              const accounts = msalInstance.getAllAccounts();
              if (accounts.length === 0) {
                console.error('[API Service] No authenticated accounts found. Cannot refresh token.');
                tokenRetryGuard.recordFailure();
                window.dispatchEvent(new CustomEvent('auth:logout'));
                return Promise.reject(error);
              }
              
              // Set active account if accounts exist but no active account is set
              if (!msalInstance.getActiveAccount() && accounts.length > 0 && accounts[0]) {
                msalInstance.setActiveAccount(accounts[0]);
              }
              
              const account = msalInstance.getActiveAccount();
              if (!account) {
                throw new Error("Active account not set for silent authentication");
              }
              
              console.log('[API Service] Attempting to acquire token silently for account:', account.username);
              
              // Use centralized token acquisition with deduplication
              // Note: forceRefresh is not supported in deduplicated version
              // If force refresh is needed, clear cache first or use direct call
              const response = await acquireTokenSilentDeduplicated(
                msalInstance,
                account,
                getAuthScopes()
              );
              
              // Record success in retry guard
              tokenRetryGuard.recordSuccess();
              
              console.log('[API Service] Token refresh successful, retrying request...');
              
              // Clear token cache to force fresh token on next request
              clearTokenCache();
              
              // Update authorization header
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
              }
              
              // Retry original request
              return client(originalRequest);
            } catch (refreshError) {
              console.error('[API Service] Token refresh failed:', refreshError);
              
              // Record failure in retry guard
              tokenRetryGuard.recordFailure();
              
              if (import.meta.env.DEV) {
                console.error('[API Service] Refresh error details:', {
                  error: refreshError instanceof Error ? refreshError.message : 'Unknown error',
                  name: refreshError instanceof Error ? refreshError.name : 'Unknown',
                  circuitOpen: tokenRetryGuard.isCircuitOpen(),
                });
              }
              
              // Determine if this is a recoverable error
              const isInteractionRequired = refreshError instanceof Error && 
                (refreshError.message.includes('interaction_required') ||
                 refreshError.message.includes('consent_required') ||
                 refreshError.message.includes('login_required'));
              
              if (isInteractionRequired || tokenRetryGuard.isCircuitOpen()) {
                // Non-recoverable - trigger logout
                console.error('[API Service] Token refresh failed - redirecting to login');
                window.dispatchEvent(new CustomEvent('auth:logout'));
              }
              
              return Promise.reject(refreshError);
            }
          } else {
            console.error('[API Service] MSAL instance not available for token refresh');
            tokenRetryGuard.recordFailure();
          }
        }
        
        // Handle other specific error codes
        if (error.response.status === 401) {
          console.error('[API Service] Unauthorized - token may be invalid or expired');
          if (import.meta.env.DEV) {
            console.error('[API Service] Check that:');
            console.error('  1. User is authenticated');
            console.error('  2. Token has required scopes (e.g., access_as_user)');
            console.error('  3. Backend API is configured to accept the token');
            console.error('  4. Token audience matches backend API client ID');
          }
        } else if (error.response.status === 403) {
          console.error('[API Service] Forbidden - insufficient permissions');
        } else if (error.response.status === 404) {
          console.error('[API Service] Resource not found');
        } else if (error.response.status >= 500) {
          console.error('[API Service] Server error');
        }
      } else if (error.request) {
        // Request made but no response received
        console.error('[API Service] No response from server:', error.message);
        
        if (error.config?.url) {
          logApiError(error.config.url, 'No response from server', 0);
        }
      } else {
        // Error in request setup
        console.error('[API Service] Request setup error:', error.message);
      }

      return Promise.reject(error);
    }
  );

  // Session idle timer — added last so it runs first (axios LIFO). Skips pending-approvals poll.
  client.interceptors.request.use(
    (config) => {
      notifySessionApiActivityForRequest(config.url);
      return config;
    },
    undefined,
    { synchronous: true }
  );
};

/**
 * Track if interceptors have been set up (singleton guard)
 * Prevents interceptor stacking
 */
let interceptorsSetup = false;

/**
 * Setup authentication interceptors for all clients
 * 
 * CRITICAL (Item 4 Fix): Must prevent interceptor stacking
 * - Uses singleton guard to setup interceptors only once
 * - Updates shared getTokenRef instead of creating new interceptors
 * 
 * @param getToken - Optional token provider function
 *                   If provided, interceptor will use this instead of getValidAccessToken()
 *                   This allows redirect handler to provide token without depending on activeAccount
 */
export const setupApiInterceptor = (getToken?: () => Promise<string | null>) => {
  // Update shared token provider reference (prevents interceptor stacking)
  getTokenRef = getToken;
  
  // Setup interceptors once (singleton guard)
  if (!interceptorsSetup) {
    setupAuthInterceptor(authClient);
    setupAuthInterceptor(credentialingClient);
    setupAuthInterceptor(dataAccessClient);
    setupAuthInterceptor(userClient);
    interceptorsSetup = true;
  }
};

/**
 * User Profile Response
 */
export interface UserProfile {
  userId: string;
  email: string;
  name: string;
  givenName?: string;
  familyName?: string;
  roles: string[];
  groups: string[];
  scopes: string[];
  isAuthenticated: boolean;
  authenticationType?: string;
}

/**
 * Token Claims Response
 */
export interface TokenClaims {
  claims: Array<{
    type: string;
    value: string;
  }>;
  count: number;
}

/**
 * Token Verification Response
 */
export interface TokenVerification {
  valid: boolean;
  userId?: string;
  email?: string;
  timestamp: string;
}

/**
 * Document Model
 */
export interface Document {
  id: number;
  title: string;
  type: string;
  status: string;
  createdDate: string;
  lastModifiedDate: string;
  userId: string;
  content?: {
    sections: Array<{
      id: number;
      title: string;
      completed: boolean;
    }>;
  };
}

/**
 * Document List Response
 */
export interface DocumentListResponse {
  documents: Document[];
  count: number;
}

/**
 * Create Document Request
 */
export interface CreateDocumentRequest {
  title: string;
  type?: string;
}

/**
 * Update Document Request
 */
export interface UpdateDocumentRequest {
  title: string;
  type?: string;
  status?: string;
}

/**
 * Health Check Response
 */
export interface HealthCheckResponse {
  status: string;
  timestamp: string;
  version: string;
}

/**
 * API Service
 */
export const api = {
  /**
   * Health Check (uses credentialing API)
   */
  health: {
    /**
     * Check API health status (public endpoint - no auth required)
     */
    check: () => credentialingClient.get<HealthCheckResponse>('/api/health'),
  },

  /**
   * User Endpoints (uses auth service)
   */
  user: {
    /**
     * Get current user profile from token claims
     */
    getProfile: () => authClient.get<UserProfile>(AUTH_API_ENDPOINTS.userProfile),

    /**
     * Get all token claims (for debugging)
     */
    getClaims: () => authClient.get<TokenClaims>(AUTH_API_ENDPOINTS.userClaims),

    /**
     * Verify token validity
     */
    verify: () => authClient.get<TokenVerification>(AUTH_API_ENDPOINTS.userVerify),
  },

  /**
   * Session Endpoints (uses auth service - Hybrid JWT + Session Management)
   */
  session: {
    /**
     * Create or update server-side session after authentication
     */
    create: (deviceInfo?: string) =>
      authClient.post(AUTH_API_ENDPOINTS.sessionCreate, { deviceInfo }),

    /**
     * Get current session information
     */
    getCurrent: () => authClient.get(AUTH_API_ENDPOINTS.sessionCurrent),

    /**
     * Update session activity (heartbeat)
     */
    heartbeat: () => authClient.post(AUTH_API_ENDPOINTS.sessionHeartbeat),

    /**
     * Terminate session (logout)
     */
    terminate: () => authClient.post(AUTH_API_ENDPOINTS.sessionTerminate),

    /**
     * Validate if session exists and is active
     */
    validate: () => authClient.get(AUTH_API_ENDPOINTS.sessionValidate),

    /**
     * Get all active sessions (admin)
     */
    getActive: () => authClient.get(AUTH_API_ENDPOINTS.sessionActive),
  },

  /**
   * Document Endpoints (uses Data Access API)
   */
  documents: {
    /**
     * Get list of documents for current user
     */
    list: () => dataAccessClient.get<DocumentListResponse>('/api/documents'),

    /**
     * Get specific document by ID
     */
    get: (id: number) => dataAccessClient.get<Document>(`/api/documents/${id}`),

    /**
     * Create new document
     */
    create: (data: CreateDocumentRequest) =>
      dataAccessClient.post<Document>('/api/documents', data),

    /**
     * Update existing document
     */
    update: (id: number, data: UpdateDocumentRequest) =>
      dataAccessClient.put<Document>(`/api/documents/${id}`, data),

    /**
     * Delete document
     */
    delete: (id: number) => dataAccessClient.delete(`/api/documents/${id}`),
  },
};

/**
 * Test API connection
 * @returns true if API is reachable, false otherwise
 */
export const testApiConnection = async (): Promise<boolean> => {
  try {
    await api.health.check();
    console.log('[API Service] API connection test successful');
    return true;
  } catch (error) {
    console.error('[API Service] API connection test failed:', error);
    return false;
  }
};

/**
 * Export axios instances for advanced usage
 */
export { authClient, credentialingClient, dataAccessClient, userClient };
